import React from "react"
import { PenaltiesInfographicStep, PenaltiesInfographicStepColor } from "@silversea-ssc/ui-components-react"

import { plural, t } from "@lingui/macro"
import type { AdministrativeFee } from "@silversea-ssc/dotcom-read-model-types/CruisePaymentTerms"
import { Currency } from "@ssc-booking/api-contracts"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import { formatCurrency } from "../../app-core/_common/format/localization"
import { CruisePaymentTermsResponse, CancellationPeriod } from "../../app-core/_common/dot-com-api-types"

export const firstPeriodIsTypeDeposit = (paymentTerms: O.Option<CruisePaymentTermsResponse>): boolean => {
    return F.pipe(
        paymentTerms,
        O.chain((terms) => terms.cancellationPeriods),
        O.chain(ROA.head),
        O.map((x) => x.type == "Deposit"),
        O.getOrElse(F.constFalse),
    )
}

const penaltyPercentageColor = (percentage: number) =>
    percentage === 0 ? "green" : percentage === 100 ? "red" : "yellow03"

const penaltyValue = (
    period: CancellationPeriod,
    currency: Currency,
    cancellationAdminFee: AdministrativeFee | undefined,
): { color: PenaltiesInfographicStepColor; label: JSX.Element } => {
    const refundableLabel = t`refundable`

    if (period.type === "Deposit")
        return {
            label: (
                <>
                    Deposit
                    <br />
                    refundable*
                </>
            ),
            color: "yellow03",
        }

    if (period.type == "NonRefundable")
        return { label: buildLabel(t`Not`, refundableLabel, cancellationAdminFee), color: "red" }

    const penalty = period.penalty

    switch (penalty.type) {
        case "percentage":
            return {
                color: penaltyPercentageColor(penalty.percentage),
                label: buildLabel(`${100 - penalty.percentage}%`, refundableLabel, cancellationAdminFee),
            }

        case "percentageWithMaximumAmount":
            return {
                color: penaltyPercentageColor(penalty.percentage),
                label:
                    penalty.percentage == 100
                        ? buildLabel(t`Not`, refundableLabel, cancellationAdminFee)
                        : buildLabel(
                              `${100 - penalty.percentage}%`,
                              `refundable up to ${formatCurrency(penalty.maxAmount, currency)}`,
                              cancellationAdminFee,
                          ),
            }

        case "fixedAmountPerBooking":
            return {
                color: "green",
                label: buildLabel(formatCurrency(penalty.amount, currency), `per booking`, cancellationAdminFee),
            }

        case "fixedAmountPerPerson":
            return {
                color: "green",
                label: buildLabel(formatCurrency(penalty.amount, currency), `per person`, cancellationAdminFee),
            }
    }
}

const buildLabel = (
    prefix: string,
    suffix: string,
    cancellationAdminFee: AdministrativeFee | undefined,
): JSX.Element => {
    return (
        <>
            <big>
                {prefix}
                {cancellationAdminFee?.amount ? "*" : ""}
            </big>{" "}
            {suffix}
        </>
    )
}

export const toPenaltiesInfographicStep =
    (currency: Currency, cancellationAdminFee: AdministrativeFee | undefined) =>
    (
        period: CancellationPeriod,
        index: number,
        array: readonly CancellationPeriod[],
    ): React.ComponentProps<typeof PenaltiesInfographicStep> & {
        key: string
    } => {
        const prevPeriod = array[index - 1] || {}

        const { validUntilDaysBeforeDeparture = undefined } = prevPeriod
        const { color, label } = penaltyValue(period, currency, index == 0 ? cancellationAdminFee : undefined) || {}

        return {
            key: index.toString(),
            dayLabel: validUntilDaysBeforeDeparture
                ? plural(validUntilDaysBeforeDeparture, {
                      one: "# DAY",
                      other: "# DAYS",
                  })
                : undefined,
            color: color,
            label: label,
        }
    }

export const formattedAdministrativeFeeDisclaimer = (
    value: AdministrativeFee | undefined,
    currency: Currency | undefined,
): string | undefined => {
    if (!value || !currency) return undefined

    const amount = formatCurrency(value.amount, currency)
    if (value.amount <= 0) return undefined

    switch (value.type) {
        case "fixedAmountPerBooking": {
            return t({
                id: "administrative-fee-per-booking-disclaimer",
                message: `*Includes a ${amount} administrative fee per booking, which will be automatically converted to a Future Cruise Credit (FCC)`,
            })
        }

        case "fixedAmountPerPerson": {
            return t({
                id: "administrative-fee-per-person-disclaimer",
                message: `*Includes a ${amount} administrative fee per person, which will be automatically converted to a Future Cruise Credit (FCC)`,
            })
        }
    }
}
